/**
 * useAiAnalyses
 *
 * Owns the 3 core AI analysis steps (analyze / compare / optimize):
 * - results (text + structured)
 * - fingerprints used to detect stale results
 * - loading & cached-indicator flags
 * - stale booleans (derived from current CV/job vs stored fingerprint)
 * - run* actions that call /api/ai with client-side caching (localStorage)
 *
 * Persisted structured results live under keys like
 *   cv_ai_analysis_structured_<templateId>
 *   cv_ai_compare_structured_<templateId>
 *   cv_ai_optimize_structured_<templateId>
 * and their fingerprint companions
 *   cv_ai_analyze_fp_<templateId>
 *   cv_ai_compare_fp_<templateId>
 *   cv_ai_optimize_fp_<templateId>
 *
 * The hook deliberately does NOT own: generate / coverLetter / translate /
 * autoIa / parseCv / availableModels. Those will be extracted later once
 * CV/template state is also in hooks (they cross many concerns).
 */

import { useCallback, useState } from 'react';
import type { CVData } from '../types';
import type {
  AiAnalyzeStructured,
  AiCompareStructured,
  AiAtsOptimizeStructured,
} from '../lib/ai-types';
import { postJsonWithTimeout } from '../lib/fetch-utils';
import {
  createCvFingerprint,
  createCvJobFingerprint,
  getCachedStep,
  setCachedStep,
  loadStoredJson,
} from '../lib/client-cache';
import { safeLocalStorageGet, safeLocalStorageGetJson } from '../lib/safe-storage';

/**
 * Lightweight fingerprint used to flag analyses as stale when the CV or the
 * job offer has changed since they were run. Kept length-based (not content
 * hash) to match the legacy shape persisted under cv_ai_<step>_fp.
 */
export type AnalysisFingerprint = {
  versionId: string;
  resumeLen: number;
  jobLen: number;
};

type GetResumeText = (cvData: CVData) => string;

function loadStoredFp(key: string): AnalysisFingerprint | null {
  const p = safeLocalStorageGetJson<AnalysisFingerprint>(key);
  if (p && p.versionId != null && typeof p.resumeLen === 'number') return p;
  return null;
}

const MISSING_KEY_HINT =
  '\n\nSur VPS : systemctl restart atsmycv. En local : .env (OPENROUTER_API_KEY ou MINIMAX_API_KEY).';

function formatAiError(e: unknown): string {
  const msg = e instanceof Error ? e.message : 'Erreur';
  const mentionsKey =
    msg.includes('OPENROUTER_API_KEY') ||
    msg.includes('MINIMAX_API_KEY') ||
    msg.includes('Aucune IA') ||
    msg.includes('non configurée');
  const mentionsMissing =
    msg.includes('not set') || msg.includes('non configurée') || msg.includes('Définissez');
  return mentionsKey && mentionsMissing ? msg + MISSING_KEY_HINT : msg;
}

export interface UseAiAnalysesParams {
  activeTemplate: string;
  cvData: CVData;
  effectiveJobDescription: string;
  /** Serializer from CVData to plain text — supplied by the caller because it
   *  depends on i18n labels owned by the App component. */
  getResumeText: GetResumeText;
}

export interface UseAiAnalysesReturn {
  // Analyze
  aiAnalysisResult: string | null;
  aiAnalysisStructured: AiAnalyzeStructured | null;
  analysisFp: AnalysisFingerprint | null;
  isAiAnalyzing: boolean;
  showAnalyzeCached: boolean;
  isAnalyzeStale: boolean;
  setAiAnalysisStructured: (v: AiAnalyzeStructured | null) => void;

  // Compare
  aiCompareResult: string | null;
  aiCompareStructured: AiCompareStructured | null;
  compareFp: AnalysisFingerprint | null;
  isAiComparing: boolean;
  showCompareCached: boolean;
  isCompareStale: boolean;
  setAiCompareStructured: (v: AiCompareStructured | null) => void;

  // Optimize
  aiOptimizeResult: string | null;
  aiOptimizeStructured: AiAtsOptimizeStructured | null;
  optimizeFp: AnalysisFingerprint | null;
  isAiOptimizing: boolean;
  showOptimizeCached: boolean;
  isOptimizeStale: boolean;
  setAiOptimizeStructured: (v: AiAtsOptimizeStructured | null) => void;

  // Derived
  currentFingerprint: AnalysisFingerprint;

  // Actions
  runAnalyze: () => Promise<void>;
  runCompare: () => Promise<void>;
  runOptimize: () => Promise<void>;
}

export function useAiAnalyses(params: UseAiAnalysesParams): UseAiAnalysesReturn {
  const { activeTemplate, cvData, effectiveJobDescription, getResumeText } = params;

  // Rehydrate from localStorage at mount (template active at that time).
  const initialTemplate =
    (typeof window !== 'undefined' && safeLocalStorageGet('cv_active_template')) ||
    activeTemplate ||
    'classique';

  // ---- analyze ----
  const [aiAnalysisResult, setAiAnalysisResult] = useState<string | null>(null);
  const [aiAnalysisStructured, setAiAnalysisStructured] = useState<AiAnalyzeStructured | null>(
    () => loadStoredJson<AiAnalyzeStructured>(`cv_ai_analysis_structured_${initialTemplate}`),
  );
  const [analysisFp, setAnalysisFp] = useState<AnalysisFingerprint | null>(() =>
    loadStoredFp('cv_ai_analyze_fp'),
  );
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [showAnalyzeCached, setShowAnalyzeCached] = useState(() =>
    Boolean(safeLocalStorageGet('cv_ai_analysis_structured')),
  );

  // ---- compare ----
  const [aiCompareResult, setAiCompareResult] = useState<string | null>(null);
  const [aiCompareStructured, setAiCompareStructured] = useState<AiCompareStructured | null>(
    () => loadStoredJson<AiCompareStructured>(`cv_ai_compare_structured_${initialTemplate}`),
  );
  const [compareFp, setCompareFp] = useState<AnalysisFingerprint | null>(() =>
    loadStoredFp('cv_ai_compare_fp'),
  );
  const [isAiComparing, setIsAiComparing] = useState(false);
  const [showCompareCached, setShowCompareCached] = useState(() =>
    Boolean(safeLocalStorageGet('cv_ai_compare_structured')),
  );

  // ---- optimize ----
  const [aiOptimizeResult, setAiOptimizeResult] = useState<string | null>(null);
  const [aiOptimizeStructured, setAiOptimizeStructured] =
    useState<AiAtsOptimizeStructured | null>(() =>
      loadStoredJson<AiAtsOptimizeStructured>(`cv_ai_optimize_structured_${initialTemplate}`),
    );
  const [optimizeFp, setOptimizeFp] = useState<AnalysisFingerprint | null>(() =>
    loadStoredFp('cv_ai_optimize_fp'),
  );
  const [isAiOptimizing, setIsAiOptimizing] = useState(false);
  const [showOptimizeCached, setShowOptimizeCached] = useState(() =>
    Boolean(safeLocalStorageGet('cv_ai_optimize_structured')),
  );

  // ---- derived ----
  const currentFingerprint: AnalysisFingerprint = {
    versionId: activeTemplate,
    resumeLen: getResumeText(cvData).length,
    jobLen: (effectiveJobDescription || '').trim().length,
  };

  const isStale = (fp: AnalysisFingerprint | null, structured: unknown): boolean =>
    Boolean(
      structured &&
        fp &&
        (fp.versionId !== currentFingerprint.versionId ||
          fp.resumeLen !== currentFingerprint.resumeLen ||
          fp.jobLen !== currentFingerprint.jobLen),
    );

  const isAnalyzeStale = isStale(analysisFp, aiAnalysisStructured);
  const isCompareStale = isStale(compareFp, aiCompareStructured);
  const isOptimizeStale = isStale(optimizeFp, aiOptimizeStructured);

  // ---- actions ----
  const runAnalyze = useCallback(async () => {
    setIsAiAnalyzing(true);
    setShowAnalyzeCached(false);
    setAiAnalysisResult(null);
    const resumeText = getResumeText(cvData);
    const fingerprint = createCvFingerprint(activeTemplate, resumeText);

    const cached = getCachedStep<AiAnalyzeStructured>(
      `cv_ai_analysis_structured_${activeTemplate}`,
      `cv_ai_analyze_fp_${activeTemplate}`,
      fingerprint,
    );
    if (cached) {
      setAiAnalysisResult('[Résultat en cache]');
      setAiAnalysisStructured(cached.data);
      setShowAnalyzeCached(true);
      setAnalysisFp({ versionId: activeTemplate, resumeLen: resumeText.length, jobLen: 0 });
      setIsAiAnalyzing(false);
      return;
    }

    try {
      const { res, json } = await postJsonWithTimeout<{
        error?: string;
        analysis?: string;
        structured?: unknown;
      }>('/api/ai', {
        route: 'analyze',
        resumeText,
        jobDescription: effectiveJobDescription || undefined,
      });
      if (!res.ok) throw new Error(json.error || 'Erreur analyse IA');
      setAiAnalysisResult(json.analysis ?? '');
      const structured = json.structured as AiAnalyzeStructured | null;
      setAiAnalysisStructured(structured ?? null);
      if (structured) {
        const fp: AnalysisFingerprint = {
          versionId: activeTemplate,
          resumeLen: resumeText.length,
          jobLen: (effectiveJobDescription || '').trim().length,
        };
        setAnalysisFp(fp);
        setCachedStep(
          `cv_ai_analysis_structured_${activeTemplate}`,
          `cv_ai_analyze_fp_${activeTemplate}`,
          fingerprint,
          structured,
        );
      }
    } catch (e) {
      console.error('[Analyse CV] Error:', e);
      setAiAnalysisResult(formatAiError(e));
      setAiAnalysisStructured(null);
    } finally {
      setIsAiAnalyzing(false);
    }
  }, [activeTemplate, cvData, effectiveJobDescription, getResumeText]);

  const runCompare = useCallback(async () => {
    if (!effectiveJobDescription?.trim()) {
      setAiCompareResult(
        'Collez ou saisissez une offre d\'emploi dans la section "Offre d\'emploi cible" puis réessayez.',
      );
      return;
    }
    setIsAiComparing(true);
    setShowCompareCached(false);
    setAiCompareResult(null);
    const resumeText = getResumeText(cvData);
    const fingerprint = createCvJobFingerprint(
      activeTemplate,
      resumeText,
      effectiveJobDescription || '',
    );

    const cached = getCachedStep<AiCompareStructured>(
      `cv_ai_compare_structured_${activeTemplate}`,
      `cv_ai_compare_fp_${activeTemplate}`,
      fingerprint,
    );
    if (cached) {
      setAiCompareResult('[Résultat en cache]');
      setAiCompareStructured(cached.data);
      setShowCompareCached(true);
      setCompareFp({
        versionId: activeTemplate,
        resumeLen: resumeText.length,
        jobLen: (effectiveJobDescription || '').trim().length,
      });
      setIsAiComparing(false);
      return;
    }

    try {
      const { res, json } = await postJsonWithTimeout<{
        error?: string;
        comparison?: string;
        structured?: unknown;
      }>('/api/ai', {
        route: 'compare',
        resumeText,
        jobDescription: effectiveJobDescription,
      });
      if (!res.ok) throw new Error(json.error || 'Erreur comparaison IA');
      setAiCompareResult(json.comparison ?? '');
      const structured = json.structured as AiCompareStructured | null;
      setAiCompareStructured(structured ?? null);
      if (structured) {
        const fp: AnalysisFingerprint = {
          versionId: activeTemplate,
          resumeLen: resumeText.length,
          jobLen: (effectiveJobDescription || '').trim().length,
        };
        setCompareFp(fp);
        setCachedStep(
          `cv_ai_compare_structured_${activeTemplate}`,
          `cv_ai_compare_fp_${activeTemplate}`,
          fingerprint,
          structured,
        );
      }
    } catch (e) {
      console.error('[Comparaison CV / offre] Error:', e);
      setAiCompareResult(formatAiError(e));
      setAiCompareStructured(null);
    } finally {
      setIsAiComparing(false);
    }
  }, [activeTemplate, cvData, effectiveJobDescription, getResumeText]);

  const runOptimize = useCallback(async () => {
    if (!effectiveJobDescription?.trim()) {
      setAiOptimizeResult(
        'Collez ou saisissez une offre d\'emploi dans la section "Offre d\'emploi cible" puis réessayez.',
      );
      return;
    }
    setIsAiOptimizing(true);
    setShowOptimizeCached(false);
    setAiOptimizeResult(null);
    const resumeText = getResumeText(cvData);
    const fingerprint = createCvJobFingerprint(
      activeTemplate,
      resumeText,
      effectiveJobDescription || '',
    );

    const cached = getCachedStep<AiAtsOptimizeStructured>(
      `cv_ai_optimize_structured_${activeTemplate}`,
      `cv_ai_optimize_fp_${activeTemplate}`,
      fingerprint,
    );
    if (cached) {
      setAiOptimizeResult('[Résultat en cache]');
      setAiOptimizeStructured(cached.data);
      setShowOptimizeCached(true);
      setOptimizeFp({
        versionId: activeTemplate,
        resumeLen: resumeText.length,
        jobLen: (effectiveJobDescription || '').trim().length,
      });
      setIsAiOptimizing(false);
      return;
    }

    try {
      const { res, json } = await postJsonWithTimeout<{
        error?: string;
        report?: string;
        structured?: unknown;
      }>('/api/ai', {
        route: 'ats-optimize',
        action: 'optimize',
        resumeText,
        jobDescription: effectiveJobDescription,
      });
      if (!res.ok) throw new Error(json.error || 'Erreur optimisation ATS IA');
      setAiOptimizeResult(json.report ?? '');
      const structured = json.structured as AiAtsOptimizeStructured | null;
      setAiOptimizeStructured(structured ?? null);
      if (structured) {
        const fp: AnalysisFingerprint = {
          versionId: activeTemplate,
          resumeLen: resumeText.length,
          jobLen: (effectiveJobDescription || '').trim().length,
        };
        setOptimizeFp(fp);
        setCachedStep(
          `cv_ai_optimize_structured_${activeTemplate}`,
          `cv_ai_optimize_fp_${activeTemplate}`,
          fingerprint,
          structured,
        );
      }
    } catch (e) {
      console.error('[Rapport ATS IA] Error:', e);
      setAiOptimizeResult(formatAiError(e));
      setAiOptimizeStructured(null);
    } finally {
      setIsAiOptimizing(false);
    }
  }, [activeTemplate, cvData, effectiveJobDescription, getResumeText]);

  return {
    aiAnalysisResult,
    aiAnalysisStructured,
    analysisFp,
    isAiAnalyzing,
    showAnalyzeCached,
    isAnalyzeStale,
    setAiAnalysisStructured,

    aiCompareResult,
    aiCompareStructured,
    compareFp,
    isAiComparing,
    showCompareCached,
    isCompareStale,
    setAiCompareStructured,

    aiOptimizeResult,
    aiOptimizeStructured,
    optimizeFp,
    isAiOptimizing,
    showOptimizeCached,
    isOptimizeStale,
    setAiOptimizeStructured,

    currentFingerprint,

    runAnalyze,
    runCompare,
    runOptimize,
  };
}
